import { useWorkspaceText } from './interface-copy';
import { useEffect, useId, useRef, useState } from 'react';
import { Popover } from 'radix-ui';
import { useCompany } from '@/company/context';
import { useOptionalWorkspace, type WorkspaceState } from './context';
import { WorkspaceIcon } from './icons';
import type { WorkspaceTurnContext } from './types';
import { useContextPickerKeyboard } from './picker-keyboard';
import { WorkspaceContextChips } from './context-reference-chips';
import { fileAttachmentStatus, skillContext, WORKSPACE_ATTACHMENT_LIMIT } from './skill-context';
import { UPLOAD_ACCEPT, workspaceApi } from './api';
import { useWorkspaceFind } from './retrieval';

export function contextToken(text: string) {
  const match = text.match(/(?:^|\s)([@/])([^\s@/]*)$/);
  return match
    ? {
        kind: match[1] === '/' ? ('skills' as const) : ('files' as const),
        query: match[2],
        start: text.length - match[1].length - match[2].length,
      }
    : null;
}
type ComposerToolsProps = {
  text: string;
  setText: (text: string) => void;
  disabled?: boolean;
  variant?: 'all' | 'chips' | 'buttons';
};
/** Mount inside native composer; text and setText are its existing runtime state. */
export function WorkspaceComposerTools(props: ComposerToolsProps) {
  const ws = useOptionalWorkspace();
  return ws ? <ActiveComposerTools {...props} ws={ws} /> : null;
}
function ActiveComposerTools({
  text,
  setText,
  disabled = false,
  variant = 'all',
  ws,
}: ComposerToolsProps & { ws: WorkspaceState }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const company = useCompany();
  const owner = `${ws.user?.id ?? ''}\0${company.data?.company?.id ?? ''}`;
  const [menu, setMenu] = useState<'skills' | 'files' | null>(null);
  const [query, setQuery] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const anchor = useRef<HTMLDivElement>(null);
  const picker = useRef<HTMLDivElement>(null);
  const optionsId = useId();
  const [selected, setSelected] = useState(0);
  const previous = useRef(text);
  const selectionScope = useRef<{ key: string; context: WorkspaceTurnContext } | null>(null);
  useEffect(
    () => () => {
      selectionScope.current = null;
    },
    [],
  );
  useEffect(() => {
    if (variant === 'chips' || disabled || text === previous.current) return;
    previous.current = text;
    const token = contextToken(text);
    setMenu(token?.kind ?? null);
    setQuery(token?.query ?? '');
    setSelected(0);
  }, [text, disabled, variant]);
  const context = ws.turnContext;
  const skill = ws.data.skills.find((item) => item.id === context.intent.skillId);
  const retrieval = useWorkspaceFind(
    menu !== null && !disabled && Boolean(company.data?.company?.id),
    query,
    menu === 'skills' ? ['skill'] : ['file'],
    owner,
  );
  const selectionKey = `${menu ?? ''}\0${query}\0${owner}\0${ws.threadId}\0${disabled}`;
  if (selectionScope.current?.key !== selectionKey || selectionScope.current.context !== context)
    selectionScope.current = { key: selectionKey, context };
  const change = (next: WorkspaceTurnContext) => ws.setTurnContext(next);
  const clearToken = () => {
    const token = contextToken(text);
    if (token) setText(text.slice(0, token.start));
    setMenu(null);
    anchor.current
      ?.closest('.aui-composer-root')
      ?.querySelector<HTMLTextAreaElement>('textarea, input:not([type="file"])')
      ?.focus();
  };
  const hits = retrieval.hits;
  const options = hits.map((hit) => {
    const file = ws.data.files.find((item) => item.id === hit.id);
    const status = menu === 'files' ? fileAttachmentStatus(context, skill, hit.id) : null;
    return {
      id: hit.id,
      kind: hit.kind,
      name: hit.title,
      description:
        menu === 'skills'
          ? (hit.excerpt ??
            ('matchReason' in hit ? hit.matchReason : undefined) ??
            uiText('Published Skill'))
          : status === 'saved'
            ? uiText('Included with Skill')
            : status === 'attached'
              ? uiText('Already attached')
              : file?.includeInKnowledge
                ? uiText('Also available in company knowledge')
                : (hit.excerpt ?? uiText('Company file')),
    };
  });
  const choose = async (index: number) => {
    const item = options[index];
    if (!item) return;
    if (menu === 'skills') {
      const request = { key: selectionKey, context };
      selectionScope.current = request;
      let selected = ws.data.skills.find((candidate) => candidate.id === item.id);
      try {
        if (!selected || typeof selected.draft.instructions !== 'string')
          selected = await workspaceApi.skill(item.id);
      } catch {
        if (selectionScope.current === request)
          ws.notify(uiText('This Skill is unavailable. Try again.'));
        return;
      }
      if (selectionScope.current !== request) return;
      if (!selected?.published) return;
      change(skillContext(selected, 'skill-run', context));
    } else {
      if (item.kind !== 'file') {
        if (!context.sourceIds.includes(item.id))
          change({ ...context, sourceIds: [...context.sourceIds, item.id] });
        clearToken();
        return;
      }
      const status = fileAttachmentStatus(context, skill, item.id);
      if (status === 'full') {
        ws.notify(
          uiText('You can attach up to {value1} files. Remove an attachment to add another.', {
            value1: WORKSPACE_ATTACHMENT_LIMIT,
          }),
        );
        return;
      }
      if (status !== 'attached') change({ ...context, fileIds: [...context.fileIds, item.id] });
    }
    clearToken();
  };
  useContextPickerKeyboard({
    open: menu !== null && !disabled && variant !== 'chips',
    anchor,
    picker,
    count: options.length,
    selected,
    onMove: setSelected,
    onChoose: choose,
    onClose: () => setMenu(null),
  });
  return (
    <>
      {variant !== 'buttons' && <WorkspaceContextChips ws={ws} disabled={disabled} />}
      {variant !== 'chips' && (
        <div className="ws-composer-tools">
          <Popover.Root open={menu !== null} onOpenChange={(open) => !open && setMenu(null)}>
            <Popover.Anchor asChild>
              <div className="ws-composer-tool-buttons" ref={anchor}>
                <button
                  type="button"
                  className="ws-icon-button"
                  aria-label={uiText('Add company context')}
                  title={uiText('Add company context · @')}
                  disabled={disabled}
                  onClick={() => {
                    setMenu(menu === 'files' ? null : 'files');
                    setQuery('');
                    setSelected(0);
                  }}
                >
                  <WorkspaceIcon name="files" size={18} />
                </button>
                <button
                  type="button"
                  className="ws-icon-button"
                  aria-label={uiText('Choose a Skill')}
                  title={uiText('Choose a Skill · /')}
                  disabled={disabled}
                  onClick={() => {
                    setMenu(menu === 'skills' ? null : 'skills');
                    setQuery('');
                    setSelected(0);
                  }}
                >
                  <WorkspaceIcon name="skills" size={19} />
                </button>
                <span className="ws-composer-hint">
                  <kbd>@</kbd> {uiText('context')}
                  <span>·</span> <kbd>/</kbd> {uiText('skills')}
                </span>
              </div>
            </Popover.Anchor>
            <Popover.Portal>
              <Popover.Content
                ref={picker}
                className="ws-context-picker"
                side="top"
                align="start"
                sideOffset={16}
                onOpenAutoFocus={(event) => event.preventDefault()}
                onCloseAutoFocus={(event) => event.preventDefault()}
                aria-label={
                  menu === 'skills' ? uiText('Choose a Skill') : uiText('Choose company context')
                }
              >
                <header>
                  {menu === 'skills'
                    ? uiText('Skills for this conversation')
                    : uiText('Bring context into the conversation')}
                  <button
                    type="button"
                    className="ws-icon-button"
                    onClick={() => setMenu(null)}
                    aria-label={uiText('Close context picker')}
                  >
                    <WorkspaceIcon name="close" size={14} />
                  </button>
                </header>
                <input
                  aria-label={uiText('Filter company context')}
                  type="text"
                  placeholder={menu === 'skills' ? uiText('Find a Skill…') : uiText('Find a file…')}
                  value={query}
                  role="combobox"
                  aria-expanded="true"
                  aria-controls={optionsId}
                  aria-activedescendant={
                    options.length
                      ? `${optionsId}-${Math.min(selected, options.length - 1)}`
                      : undefined
                  }
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setSelected(0);
                  }}
                />
                <div
                  className="ws-context-options"
                  role="listbox"
                  id={optionsId}
                  aria-label={uiText('Available company context')}
                  aria-busy={retrieval.loading}
                >
                  {options.map((item, index) => (
                    <button
                      type="button"
                      key={`${item.kind}:${item.id}`}
                      id={`${optionsId}-${index}`}
                      role="option"
                      aria-selected={index === Math.min(selected, options.length - 1)}
                      aria-disabled={
                        menu === 'files' && fileAttachmentStatus(context, skill, item.id) === 'full'
                      }
                      onMouseEnter={() => setSelected(index)}
                      onClick={() => choose(index)}
                    >
                      <WorkspaceIcon name={menu === 'skills' ? 'skills' : 'file'} />
                      <span>
                        <strong>{item.name}</strong>
                        <small>{item.description}</small>
                      </span>
                      <WorkspaceIcon name="arrow" size={14} />
                    </button>
                  ))}
                  {!options.length && !retrieval.loading && !retrieval.error && (
                    <p>
                      {menu === 'skills'
                        ? uiText('No matching published Skills.')
                        : uiText('No matching files.')}
                    </p>
                  )}
                </div>
                {retrieval.loading && <p role="status">{uiText('Searching company work…')}</p>}
                {retrieval.error && (
                  <div role="alert">
                    <p>{retrieval.error}</p>
                    <button type="button" onClick={retrieval.retry}>
                      {uiText('Try search again')}
                    </button>
                  </div>
                )}
                {retrieval.degraded && (
                  <p role="status">
                    {uiText(
                      'Meaning search is temporarily limited. Showing available text matches.',
                    )}
                  </p>
                )}
                {retrieval.hasMore && !retrieval.loading && !retrieval.error && (
                  <button type="button" onClick={retrieval.loadMore}>
                    {uiText('Load more matches')}
                  </button>
                )}
                {menu === 'files' && context.fileIds.length >= WORKSPACE_ATTACHMENT_LIMIT && (
                  <p className="ws-picker-note" role="status">
                    {uiRich(
                      '{value1} files attached. Remove an attachment to add another. Saved Skill references are included separately.',
                      { value1: WORKSPACE_ATTACHMENT_LIMIT },
                    )}
                  </p>
                )}
                <button
                  className="ws-picker-action"
                  type="button"
                  onClick={() => {
                    const skills = menu === 'skills';
                    setMenu(null);
                    skills ? ws.authorSkill() : input.current?.click();
                  }}
                >
                  <WorkspaceIcon name="plus" size={14} />
                  {menu === 'skills'
                    ? uiText('Create a Skill in chat')
                    : uiText('Upload a company file')}
                </button>
                <p className="ws-picker-note">
                  {uiText('Company knowledge is always available to Poneva.')}
                </p>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
          <input
            ref={input}
            type="file"
            className="ws-sr-only"
            multiple
            accept={UPLOAD_ACCEPT}
            onChange={(event) => {
              if (event.target.files) void ws.upload(event.target.files, false, { attach: true });
              event.target.value = '';
            }}
          />
        </div>
      )}
    </>
  );
}
